import { VatOverview } from 'components/Vat/Overview';
import * as React from 'react';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrenciesRequest } from 'store/common/actions';
import { getCommonCurrencies, getCurrenciesLoaded } from 'store/common/selectors';
import {
  getOrgMappings,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { fetchRunDataRequest } from 'store/vat/actions';
import { previewTifdReportRequest } from 'store/tifd/actions';
import { VatRunRequestData } from 'store/vat/models';
import { getVatLoading } from 'store/vat/selectors';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { Form } from 'antd';
import { turnDrawerOff } from 'store/app/actions';
import { getAuthUser } from 'store/auth/selectors';
import { isMaltaTifdReturnType } from 'utils/tifd-mapping';
import {
  Preference,
  PreferenceActions,
  usePreferences,
} from 'utils/preferences';
import {
  PreferenceSections,
  resolveReportingCurrencyFromPreferences,
  saveReportingCurrencyPreference,
} from './vat-overview-preferences';

interface VatOverviewPageProps {
  connectionId: string;
  mappingId: string;
  groupContext?: { fromGroup?: boolean; groupId?: string };
}
export const VatOverviewPage: React.FC<VatOverviewPageProps> = ({
  connectionId,
  mappingId,
  groupContext,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const dispatch = useDispatch();
  const preferences = usePreferences();
  const currentUser = useSelector(getAuthUser);
  const loading = useSelector(getVatLoading);
  const currencies = useSelector(getCommonCurrencies);
  const currenciesLoaded = useSelector(getCurrenciesLoaded);
  const organisations = useSelector(getOrgOrganisations);
  const mappings = useSelector(getOrgMappings);
  const [form] = Form.useForm();
  const selectedCurrency = organisations?.find(
    (o) => o.connectionId === connectionId
  )?.currency;
  const selectedMapping = mappings?.find((m) => m.mappingId === mappingId);
  const returnTypeId = selectedMapping?.returnTypeId;
  const retFreq = selectedMapping?.returnFrequency
    ? selectedMapping?.returnFrequency
    : '';
  const selectedRange = calculateNextPeriod(
    selectedMapping?.periodFromDate,
    retFreq
  )[2];

  const handleVatReportPreferences = React.useCallback(
    (action: PreferenceActions, preference: Preference) => {
      if (!currentUser) {
        return action === PreferenceActions.GET ? undefined : false;
      }
      return preferences(
        action,
        PreferenceSections.VAT_REPORT,
        preference,
        currentUser.userUuid
      );
    },
    [currentUser, preferences]
  );

  const saveCurrencyPreference = React.useCallback(
    (currency: string) => {
      if (returnTypeId == null || returnTypeId === '') return;
      saveReportingCurrencyPreference(
        handleVatReportPreferences,
        connectionId,
        returnTypeId,
        currency
      );
    },
    [connectionId, handleVatReportPreferences, returnTypeId]
  );

  const entityName = organisations?.find(
    (o) => o.connectionId === connectionId
  )?.friendlyName;
  const retTypes = organisations?.find(
    (o) => o.connectionId === connectionId
  )?.returnTypesEdit;
  const taxReturn = retTypes?.find((rt) => rt.id === returnTypeId);
  const taxReturnName = taxReturn?.countryName
    ? `${localizeCountry(taxReturn.countryName, taxReturn.countryName)} - ${taxReturn?.name}`
    : taxReturn?.name;

  const generateTaxReport = (dateRange: Date[], currency: string) => {
    saveCurrencyPreference(currency);

    const fromDate = moment(new Date(dateRange[0])).format('YYYY-MM-DD');
    const toDate = moment(new Date(dateRange[1])).format('YYYY-MM-DD');

    if (isMaltaTifdReturnType(returnTypeId)) {
      const organisation = organisations?.find(
        (item) => item.connectionId === connectionId
      );
      dispatch(
        previewTifdReportRequest({
          applicationConnectionId: organisation?.uuid ?? '',
          periodStart: fromDate,
          periodEnd: toDate,
          connectionId,
          mappingId,
          currency,
          providerName: organisation?.providerName,
          connectionName: organisation?.friendlyName,
          reportingTypeId: returnTypeId,
          taxReportTypeName: taxReturn?.name,
          jsonMapping: selectedMapping?.jsonMapping,
          shortCode: organisation?.shortCode,
          groupContext,
        })
      );
      dispatch(turnDrawerOff());
      return;
    }

    const vatRunRequestData: VatRunRequestData = {
      connectionId,
      mappingId,
      currency,
      fromDate,
      toDate,
      provider: '',
    };

    sessionStorage.setItem(
      'last-created-report',
      JSON.stringify(vatRunRequestData)
    );
    dispatch(fetchRunDataRequest(vatRunRequestData, groupContext, true));
    dispatch(turnDrawerOff());
  };

  React.useEffect(() => {
    if (currenciesLoaded) return;
    dispatch(fetchCurrenciesRequest());
  }, [currenciesLoaded, dispatch]);

  React.useEffect(() => {
    const resolvedCurrency = resolveReportingCurrencyFromPreferences(
      handleVatReportPreferences,
      connectionId,
      returnTypeId,
      selectedCurrency,
      currencies
    );
    const updates: { currency?: string; dateRange?: Date[] } = {};

    if (resolvedCurrency) {
      updates.currency = resolvedCurrency;
    }
    if (selectedRange) {
      updates.dateRange = selectedRange;
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [
    connectionId,
    returnTypeId,
    selectedCurrency,
    selectedRange,
    currencies,
    form,
    handleVatReportPreferences,
  ]);

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('returns.mapping.tax-return')} | ${process.env.PROJECT_NAME}`}</title>
        <meta name="description" content={t('users.page-meta-description')} />
      </Helmet>

      <VatOverview
        loading={loading}
        currencies={currencies.sort()}
        generateTaxReport={generateTaxReport}
        form={form}
        onCurrencyChange={saveCurrencyPreference}
        entityName={entityName}
        taxReturnName={taxReturnName}
      />
    </React.Fragment>
  );
};
